import styled from '@emotion/styled';
import InlineMessage from '@ingka/inline-message';
import Loading, { LoadingBall } from '@ingka/loading';
import infoIcon from '@ingka/ssr-icon/paths/information';
import Status from '@ingka/status';
import React from 'react';

import { tokens } from '../../../shared/styles/core/tokens';
import { useConnectedItemsState } from '../../items/state/connected-items.state';
import { useDuplicatePartsModalState } from '../../items/state/duplicate-parts-modal.state';
import type { RefreshResponseItem } from '../../items/types/items.api';

type OperationType = 'refresh' | 'reset';

interface RowPresentation {
   loading: boolean;
   colour: 'green' | 'red' | 'grey';
   text: string;
}

const count = (n: number, noun: string): string => `${n} ${noun}${n === 1 ? '' : 's'}`;

const getRowPresentation = (
   response: RefreshResponseItem | undefined,
   children: RefreshResponseItem[],
   anyCompleted: boolean,
   operationType: OperationType
): RowPresentation => {
   if (response !== undefined) {
      return {
         loading: false,
         colour: response.status ? 'green' : 'red',
         text: response.message || (response.status ? 'OK' : 'Failed'),
      };
   }

   if (children.length > 0) {
      const successful = children.filter((child) => child.status).length;
      const failed = children.length - successful;

      if (failed === 0) {
         return {
            loading: false,
            colour: 'green',
            text: operationType === 'reset' ? 'Reset complete' : 'Refresh complete',
         };
      }

      return {
         loading: false,
         colour: 'red',
         text: `${successful} ok, ${failed} failed`,
      };
   }

   return { loading: !anyCompleted, colour: 'grey', text: 'Pending...' };
};

const messageStyle: React.CSSProperties = { position: 'fixed', zIndex: 1000, left: '40%' };
const scrollAreaStyle: React.CSSProperties = { maxHeight: '20rem', overflow: 'auto' };
const tableStyle: React.CSSProperties = { borderSpacing: '0.5em' };
const statusCellStyle: React.CSSProperties = { verticalAlign: 'middle' };
const childStatusCellStyle: React.CSSProperties = { verticalAlign: 'middle', paddingLeft: '1em' };
const childItemNoCellStyle: React.CSSProperties = { paddingLeft: '1em' };
const closeButtonStyle: React.CSSProperties = {
   cursor: 'pointer',
   background: 'none',
   border: 'none',
   color: 'inherit',
   textDecoration: 'underline',
   float: 'right',
};

const ScanningRow = styled.div`
   display: flex;
   align-items: center;
   gap: ${tokens.space.sm};
   margin-top: ${tokens.space.sm};
`;

const ConnectedItemsStatusContent = (): JSX.Element => {
   const { refreshRequests, getItemsCompleted, getParentItems, getChildrenForParent, getItemResponseStatus, closeDialogue, operationType } =
      useConnectedItemsState();
   const scanning = useDuplicatePartsModalState((state) => state.scanning);

   const parentItems = operationType === 'refresh' ? getParentItems() : refreshRequests;

   const completedItems = getItemsCompleted();
   const successCount = completedItems.filter((item) => item.status).length;
   const failedCount = completedItems.length - successCount;

   const title = operationType === 'reset' ? 'Resetting connected items...' : 'Refreshing items from PIA...';

   const getStatusSummary = (): string => {
      if (completedItems.length === 0) {
         return '';
      }

      const messages: string[] = [];

      if (successCount > 0) {
         messages.push(
            operationType === 'reset' ? `${count(successCount, 'item')} reset successfully` : `${count(successCount, 'item')} refreshed`
         );
      }

      if (failedCount > 0) {
         messages.push(operationType === 'reset' ? `${count(failedCount, 'item')} failed to reset` : `${failedCount} failed`);
      }

      return messages.join(', ');
   };

   return (
      <InlineMessage
         style={messageStyle}
         body={
            <>
               {completedItems.length} of {refreshRequests.length} completed. {getStatusSummary()}
               <br />
               <div style={scrollAreaStyle}>
                  <table style={tableStyle}>
                     <tbody>
                        {parentItems.map((parentItemNo) => {
                           const children = getChildrenForParent(parentItemNo);
                           const row = getRowPresentation(
                              getItemResponseStatus(parentItemNo),
                              children,
                              completedItems.length > 0,
                              operationType
                           );

                           return (
                              <React.Fragment key={parentItemNo}>
                                 <tr>
                                    <td style={statusCellStyle}>
                                       {row.loading ? (
                                          <Loading>
                                             <LoadingBall color="emphasised" size="small" />
                                          </Loading>
                                       ) : (
                                          <Status statusColor={row.colour} />
                                       )}
                                    </td>
                                    <td style={{ fontWeight: tokens.font.weight.bold }}>{parentItemNo}</td>
                                    <td>{row.text}</td>
                                 </tr>

                                 {children.map((childItem) => (
                                    <tr key={`${parentItemNo}-${childItem.itemNo}`}>
                                       <td style={childStatusCellStyle}>
                                          <Status statusColor={childItem.status ? 'green' : 'red'} />
                                       </td>
                                       <td style={childItemNoCellStyle}>{childItem.itemNo}</td>
                                       <td style={{ color: childItem.status ? 'inherit' : 'red' }}>{childItem.message}</td>
                                    </tr>
                                 ))}
                              </React.Fragment>
                           );
                        })}
                     </tbody>
                  </table>
               </div>
               {scanning && (
                  <ScanningRow>
                     <Loading>
                        <LoadingBall color="emphasised" size="small" />
                     </Loading>
                     Checking for replaced parts...
                  </ScanningRow>
               )}
               <p>
                  <button onClick={() => closeDialogue()} style={closeButtonStyle}>
                     Close
                  </button>
               </p>
            </>
         }
         ssrIcon={infoIcon}
         title={title}
         variant="informative"
      />
   );
};

export const ConnectedItemsStatus = (): JSX.Element | null => {
   const showStatusDialogue = useConnectedItemsState((state) => state.showStatusDialogue);

   if (!showStatusDialogue) {
      return null;
   }

   return <ConnectedItemsStatusContent />;
};
